import { useOutreachOperation, type OutreachHeldEvent } from '@discovr/features';
import { Button } from '../../../../components/ui/button';
import { Check, Clock, Warning } from '../../../../design-system/icons';
import { cn } from '../../../../lib/utils';
import { draftSkipCopy } from '../draftLabels';
import { ALL_HELD_SUBTITLE, ALL_HELD_TITLE, isAddressGuardReason, riskyAddressHeldLine } from '../sendGuardCopy';
import { NOTHING_SENT_SUBTITLE, bulkResultTitle, capDeferredLines, capDeferredWhen, confirmationSubtitle, failedLines, noAddressSkipLine, noEmailWaitMessage, scheduledTimeLabel, suppressedSkipLine, unrecognisedResultLine, waitingEmailLine } from './bulkSendCopy';
import { summarizeApprove } from './approveSummary';
import { RESULT_DESC_ID, RESULT_TITLE_ID, SendingView } from './SendingView';
import { EmailRow, RecipientAvatars, WarningRow, type ConfirmationRecipient } from './sendSummaryParts';

export type { ConfirmationRecipient };

/** Where the finish button can take the admin: sent mail is in Contacted, a later send in Scheduled, an approval waiting for an email stays in Not contacted. */
export type ConfirmationTab = 'contacted' | 'scheduled' | 'not_contacted';

export interface BulkSendConfirmationProps {
  campaignId: string;
  operationId: string;
  /** Set for Schedule (Figma 680:64866); absent for Send now (680:64821). */
  scheduledFor?: string;
  zone: string;
  /** outreach_message_held events collected since the approve started (Ruling 29). */
  held: OutreachHeldEvent[];
  /** The approved drafts' creators by message id, snapshotted at approve time. */
  recipients?: Record<number, ConfirmationRecipient>;
  /** Drafts left out of the send for having no address; counted with approve's no-address skips. */
  noAddressDropped?: number;
  /** Creators dropped on the Creators step as do-not-contact; counted with approve's suppressed skips. */
  suppressedDropped?: number;
  onClose: () => void;
  onNavigate?: (tab: ConfirmationTab) => void;
}

const FINISH_LABEL: Record<ConfirmationTab, string> = { contacted: 'Go to Inbox', scheduled: 'View scheduled', not_contacted: 'Go to Not contacted' };

function skipLine(reason: string, messageIds: number[], names: Record<number, string>): string {
  const known = messageIds.map((id) => names[id]).filter((name): name is string => !!name);
  if (known.length === 0) return `${draftSkipCopy(reason)} ${messageIds.length}`;
  const rest = messageIds.length - known.length;
  return `${draftSkipCopy(reason)}: ${known.map((name) => `@${name}`).join(', ')}${rest > 0 ? ` and ${rest} more` : ''}`;
}

/** P32 (Figma 680:64821 "Sending started", 680:64866 "Scheduled"), email only. */
export function BulkSendConfirmation({ campaignId, operationId, scheduledFor, zone, held, recipients = {}, noAddressDropped = 0, suppressedDropped = 0, onClose, onNavigate }: BulkSendConfirmationProps) {
  const names = Object.fromEntries(Object.entries(recipients).map(([id, r]) => [Number(id), r.username]));
  const operation = useOutreachOperation(campaignId, operationId).data;
  // Still loading: the sending view keeps the region's heading in place (and a way out).
  if (!operation) {
    const snapshot = Object.values(recipients);
    const waiting = snapshot.filter((recipient) => recipient.waiting).length;
    return <SendingView count={snapshot.length - waiting} waiting={waiting} scheduledAt={scheduledFor ? scheduledTimeLabel(scheduledFor, zone) : undefined} onClose={onClose} />;
  }
  // approve_drafts items are keyed by message id.
  const summary = summarizeApprove(operation, { noEmailDropped: noAddressDropped, suppressedDropped });
  // Sent means a message is on its way: an applied outcome waiting for an email has none (it is reported on its own line).
  const sentIds = new Set(summary.sentIds);
  const sentCount = summary.sentIds.length;
  // One deferral per message: a replayed held event must not count twice.
  const deferredBy = new Map(held.flatMap((e) => (e.reason === 'daily_cap' && sentIds.has(e.messageId) && e.scheduledFor ? [[e.messageId, e.scheduledFor] as const] : [])));
  const deferredAt = [...deferredBy.values()];
  const guardHeldIds = [...new Set(held.flatMap((e) => (isAddressGuardReason(e.reason) && sentIds.has(e.messageId) ? [e.messageId] : [])))];
  const missingText = summary.missingText.map(({ reason, ids }) => skipLine(reason, ids, names));
  const suppressedNames = summary.suppressedIds.map((id) => names[id]).filter((n): n is string => !!n);
  const waitingNames = summary.waitingEmailIds.map((id) => names[id]).filter((n): n is string => !!n);
  const people = [...sentIds].flatMap((id) => (recipients[id] ? [{ id, ...recipients[id] }] : []));
  const nothingSent = sentCount === 0;
  const goingNow = scheduledFor ? 0 : Math.max(0, sentCount - deferredAt.length - guardHeldIds.length);
  // Every Send now email was moved by the daily cap: it reads as scheduled, never "0 emails going out now".
  const allDeferred = !scheduledFor && !nothingSent && goingNow === 0 && deferredAt.length > 0;
  const scheduled = !!scheduledFor || allDeferred;
  const earliestCap = allDeferred ? [...deferredAt].sort((a, b) => Date.parse(a) - Date.parse(b))[0] : undefined;
  const scheduledAt = scheduledFor ? scheduledTimeLabel(scheduledFor, zone) : earliestCap ? `${capDeferredWhen(earliestCap, zone, new Date())} (daily cap)` : undefined;
  // Every Send now email was held for a risky address: nothing is on its way, so it never reads "Sending started".
  const allHeld = !scheduled && !nothingSent && goingNow === 0 && guardHeldIds.length > 0;
  const onlyWaiting = nothingSent && summary.waitingEmailIds.length > 0;
  // Nothing went out: the drafts are still in Not contacted, so that is where Finish takes the admin.
  const finishTab: ConfirmationTab = nothingSent ? 'not_contacted' : scheduled ? 'scheduled' : 'contacted';
  const Icon = onlyWaiting ? Clock : nothingSent || allHeld ? Warning : scheduled ? Clock : Check;

  const warnings = [
    summary.noEmail > 0 ? noAddressSkipLine(summary.noEmail) : null,
    summary.waitingEmailIds.length > 0 ? `${waitingEmailLine(summary.waitingEmailIds.length)}${waitingNames.length ? `: ${waitingNames.map((n) => `@${n}`).join(', ')}` : ''}` : null,
    summary.suppressed > 0 ? `${suppressedSkipLine(summary.suppressed)}${suppressedNames.length ? `: ${suppressedNames.map((n) => `@${n}`).join(', ')}` : ''}` : null,
    ...missingText,
    summary.otherCount > 0 ? `${summary.otherCount} skipped — ${summary.other.map(({ reason, ids }) => skipLine(reason, ids, names)).join('; ')}` : null,
    summary.unrecognised > 0 ? unrecognisedResultLine(summary.unrecognised) : null,
    ...failedLines(operation, names),
    ...(scheduledFor ? [] : capDeferredLines(deferredAt, zone, new Date())),
    guardHeldIds.length > 0 ? riskyAddressHeldLine(guardHeldIds.length, guardHeldIds.flatMap((id) => (names[id] ? [names[id]] : []))) : null,
  ].filter((line): line is string => line !== null);

  return (
    <div className="m-auto flex w-full max-w-[520px] flex-col items-center gap-4 text-center">
      <div
        className={cn(
          'flex size-16 items-center justify-center rounded-full',
          onlyWaiting ? 'bg-(--brand-100) text-(--brand-500)' : nothingSent || allHeld ? 'bg-amber-50 text-amber-700' : scheduled ? 'bg-(--brand-100) text-(--brand-500)' : 'bg-[#e7f2e7] text-[#047857]',
        )}
      >
        <Icon size={28} weight="bold" aria-hidden="true" />
      </div>
      <div className="space-y-2">
        <h2 id={RESULT_TITLE_ID} className="text-2xl font-medium text-[#0a0a0a]">{allHeld ? ALL_HELD_TITLE : bulkResultTitle({ applied: sentCount, scheduled, waiting: summary.waitingEmailIds.length })}</h2>
        <p id={RESULT_DESC_ID} className="text-base text-muted-foreground">
          {allHeld ? ALL_HELD_SUBTITLE : onlyWaiting ? noEmailWaitMessage(summary.waitingEmailIds.length) : nothingSent ? NOTHING_SENT_SUBTITLE : confirmationSubtitle({ count: scheduled ? sentCount : goingNow, scheduledAt })}
        </p>
      </div>
      {nothingSent ? null : <RecipientAvatars people={people} />}
      <ul role="list" aria-label="Send summary" className="w-full overflow-hidden rounded-xl border border-border bg-white">
        {nothingSent ? null : <EmailRow count={sentCount} />}
        {warnings.map((line) => <WarningRow key={line}>{line}</WarningRow>)}
      </ul>
      <div className="flex gap-3">
        <Button variant="outline" className="h-10 rounded-full px-5" onClick={onClose}>Close</Button>
        {onNavigate ? (
          <Button className="h-10 rounded-full px-5" onClick={() => onNavigate(finishTab)}>{FINISH_LABEL[finishTab]}</Button>
        ) : null}
      </div>
    </div>
  );
}
